import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { useAuth } from '../auth';
import {
  getPortalBookingState,
  releasePortalPhoneBooking,
} from '../services/portalApi';
import type { PortalPhoneBooking } from '../types/api';
import { formatPhoneroUsername } from '../utils/phonero';

const phoneroPortalUrl = 'https://bn.phonero.no';

export function HomeBookingCard() {
  const { getAccessToken } = useAuth();
  const [booking, setBooking] = useState<PortalPhoneBooking | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAccessToken();
        const response = await getPortalBookingState(token);
        if (!cancelled) setBooking(response.Booking);
      } catch {
        // The normal portal status remains responsible for global API errors.
      }
    })();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  async function release(): Promise<void> {
    if (!booking || !window.confirm(`Release ${booking.PhoneNumber}?`)) return;
    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await releasePortalPhoneBooking(token, booking.BookingId);
      setBooking(null);
    } catch {
      setMessage('The telephone could not be released. Please try again.');
    } finally {
      setIsBusy(false);
    }
  }

  if (!booking) return null;

  const loginUsername = formatPhoneroUsername(booking.PhoneNumber);
  const callerDisplay = [
    booking.CallerDisplayNumberName,
    booking.CallerDisplayNumber,
  ].filter(Boolean).join(' — ');

  return (
    <section className="home-booking-card">
      <div className="home-booking-heading">
        <div>
          <p className="eyebrow">Your active booking</p>
          <h2>{loginUsername}</h2>
        </div>
        <span className="status-pill status-pill-online">
          <span className="status-dot" /> Active
        </span>
      </div>

      <div className="home-booking-details">
        <div>
          <span>Login username</span>
          <strong>{loginUsername}</strong>
        </div>
        <div>
          <span>Password</span>
          <strong className="password-cell">
            {showPassword ? booking.Password : '••••••••'}
            <button className="table-action-button" type="button"
              onClick={() => setShowPassword((current) => !current)}>
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </strong>
        </div>
        <div>
          <span>Event</span>
          <strong>{booking.EventName ?? 'Standalone booking'}</strong>
        </div>
        {callerDisplay ? (
          <div>
            <span>Caller display number</span>
            <strong>{callerDisplay}</strong>
          </div>
        ) : null}
        <div>
          <span>Available until</span>
          <strong>{formatDateTime(booking.EndUtc)}</strong>
        </div>
      </div>

      {message ? <p className="api-message" role="status">{message}</p> : null}

      <div className="home-booking-actions">
        <a className="primary-button button-link" href={phoneroPortalUrl}
          target="_blank" rel="noreferrer">
          Open Phonero portal
        </a>
        <Link className="secondary-button button-link" to="/my-booking">
          Booking details
        </Link>
        <button className="secondary-button booking-release-button"
          type="button" disabled={isBusy}
          onClick={() => void release()}>
          {isBusy ? 'Releasing…' : 'Release phone'}
        </button>
      </div>
    </section>
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}
